<template>
  <div class="about-page bg-background">

    <v-container fluid class="pa-0 about-page">

      <!-- HERO -->
      <v-container fluid class="hero-section py-16 text-center">
        <h1 class="hero-title">
          About <span class="un">un</span>Booked
        </h1>
      </v-container>

      <!-- WHAT WE DO -->
      <v-container class="py-10 bg-light">
        <v-row align="center">
          <v-col cols="12" md="6" order-md="1" align="right">
            <v-img :src="bannerImage" alt="Cruise Ship" class="rounded-xl elevation-3" style="max-width: 800px;" />
          </v-col>

          <v-col cols="12" md="6" order-md="2">
            <h2 class="text-h3 font-weight-bold mb-6 section-title">What We Do</h2>
            <p class="mb-2" style="font-size: 1.25rem; line-height: 1.8;">
              We believe amazing travel experiences shouldn’t be hard to find — or overpriced.
            </p>
            <p class="mb-16" style="font-size: 1.25rem; line-height: 1.8;">
              That’s why we created a modern travel marketplace that connects explorers directly with
              verified agents, unlocking <strong>exclusive access to unsold cruise cabins and group hotel rooms</strong>
              at unbeatable value.
            </p>

            <h2 class="text-h3 font-weight-bold mb-5 section-title">
              Our Mission
            </h2>
            <p style="font-size: 1.25rem; line-height: 1.8;">
              Turn unused space into unforgettable escapes at an affordable price.
            </p>
          </v-col>
        </v-row>
      </v-container>

      <!-- Partner Logos (continuous scroll, no pages) -->
      <v-container fluid class="py-16 text-center bg-white">
        <h2 class="text-h3 font-weight-bold mb-10 section-title">
          Who We Work With
        </h2>

        <div ref="wrapperRef" class="logo-marquee relative mx-auto" @mouseenter="paused = true"
          @mouseleave="paused = false">
          <!-- gradient edge fade (optional) -->
          <div class="edge left"></div>
          <div class="edge right"></div>

          <div ref="trackRef" class="track" :class="{ paused }">
            <!-- duplicate the list back-to-back for seamless loop -->
            <div v-for="(logo, i) in logosTwice" :key="logo.alt + '-' + i"
              class="slide d-flex align-center justify-center">
              <v-img :src="logo.src" :alt="logo.alt" height="60" contain
                class="opacity-80 hover:opacity-100 transition-ease-in-out" />
            </div>
          </div>
        </div>
      </v-container>

      <!-- What Makes Us Different -->
      <v-container fluid class="bg-hero-section py-16">
        <v-container class="white--text text-center" style="max-width: 1500px;">
          <!-- Title -->
          <v-row justify="center" class="mb-8">
            <v-col cols="12" md="8">
              <h2 class="text-h3 font-weight-bold mb-3">
                What Makes Us Different
              </h2>
              <p style="font-size: 1.25rem; line-height: 1.8;">
                Built for travelers and the professionals who serve them — with value,
                transparency, and service at the center of everything.
              </p>
            </v-col>
          </v-row>

          <!-- Cards -->
          <v-row justify="center">
            <v-col v-for="(item, index) in whatMakesUsDifferent" :key="index" cols="12" md="6" class="mb-6 d-flex">
              <v-card color="surface" class="difference-card pa-8 d-flex flex-column align-center text-center w-100"
                elevation="2">
                <!-- Icon chip -->
                <v-avatar class="difference-icon mb-4" size="75">
                  <v-icon size="45" class="text-primary">
                    {{ item.icon }}
                  </v-icon>
                </v-avatar>

                <h3 class="font-weight-bold mb-3 text-primary">
                  {{ item.title }}
                </h3>
                <p class="text-body-1" style="line-height: 1.7;">
                  {{ item.text }}
                </p>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-container>



      <!-- Call to Action -->
      <v-container fluid class="py-16 bg-white text-center">
        <v-row justify="center">
          <v-col cols="12" md="8">
            <h2 class="text-h3 font-weight-bold mb-2">For Travel Professionals</h2>
            <p class="mb-4" style="font-size: 1.85rem; line-height: 1.8;">Have Extra Space to Fill?</p>
            <p class="mb-6" style="font-size: 1.25rem; line-height: 1.8;">
              We help you list, connect, and convert—without the extra hassle. Tap into our network of eager travelers
              and
              turn your unused space into value.
            </p>
            <router-link to="/agent">
              <v-btn color="primary" size="large" class="px-10">Learn More</v-btn>
            </router-link>
          </v-col>
        </v-row>
      </v-container>

    </v-container>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

const bannerImage = new URL('@/assets/imgs/about-us.png', import.meta.url).href

const partnerLogos = [
  { src: require('@/assets/logos/carnival.png'), alt: 'Carnival Cruise' },
  { src: require('@/assets/logos/royalcaribbean.png'), alt: 'Royal Caribbean' },
  { src: require('@/assets/logos/ncl.png'), alt: 'Norwegian Cruise Line' },
  { src: require('@/assets/logos/disney.png'), alt: 'Disney Cruise Line' },
  { src: require('@/assets/logos/princess.png'), alt: 'Princess Cruises' },
  { src: require('@/assets/logos/hollandamerica.png'), alt: 'Holland America Line' },
  { src: require('@/assets/logos/virgin.png'), alt: 'Virgin Voyages' },
  { src: require('@/assets/logos/msc.png'), alt: 'MSC Cruises' },
]

const whatMakesUsDifferent = [
  {
    title: 'Professional Agent-Driven',
    text: 'We give travel professionals a powerful platform to list and monetize the group space they’ve already secured.',
    icon: 'mdi-account-tie'
  },
  {
    title: 'Curated, Exclusive Inventory',
    text: 'Every listing is vetted for quality—giving you access to group-reserved cruise cabins and hotel rooms now open to individual travelers.',
    icon: 'mdi-star-check'
  },
  {
    title: 'Streamlined & Secure',
    text: 'We handle secure payments, communication, and support—so you can focus on your trip, not logistics.',
    icon: 'mdi-shield-check'
  },
  {
    title: 'Fair, Transparent Pricing',
    text: 'No hidden fees. Just honest rates and a small service fee that keeps everything running smoothly.',
    icon: 'mdi-cash-multiple'
  }
]

const logosTwice = [...partnerLogos, ...partnerLogos]

const trackRef = ref(null)
const paused = ref(false)
const pxPerSec = ref(20)

function setMetrics() {
  const track = trackRef.value
  if (!track) return

  // width of one original cycle (first half of children)
  const half = partnerLogos.length
  let cycleWidth = 0
  for (let i = 0; i < half; i++) {
    const el = track.children[i]
    if (el) cycleWidth += el.offsetWidth
  }

  // if still zero (images not measured yet), bail and try again soon
  if (!cycleWidth) {
    // small retry after layout stabilizes
    requestAnimationFrame(setMetrics)
    return
  }

  const seconds = cycleWidth / pxPerSec.value
  track.style.setProperty('--cycle', `${cycleWidth}px`)
  track.style.setProperty('--duration', `${seconds}s`)
}

function waitForImagesThenMeasure() {
  const root = trackRef.value
  if (!root) return

  const imgs = root.querySelectorAll('img')
  let pending = imgs.length
  if (!pending) { setMetrics(); return }

  const done = () => { if (--pending === 0) setMetrics() }
  imgs.forEach(img => {
    if (img.complete) pending--
    else {
      img.addEventListener('load', done, { once: true })
      img.addEventListener('error', done, { once: true })
    }
  })
  if (pending === 0) setMetrics()
}

function onResize() { setMetrics() }

onMounted(async () => {
  await nextTick()
  // give Vuetify a tick to mount <img> inside <v-img>
  requestAnimationFrame(waitForImagesThenMeasure)
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>


<style scoped>
.about-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Extra spacing utilities to complement Vuetify’s */
.py-16 {
  padding-block: 64px;
}

.py-24 {
  padding-block: 96px;
}

.section-title {
  color: #333333;
}

.text-justify {
  font-size: large;
}

.logo-marquee {
  overflow: hidden;
  position: relative;
  max-width: 1200px;
}

/* Important: give slides a minimum width so they’re visible pre-load */
.slide {
  flex: 0 0 auto;
  min-width: 140px;
}

.track {
  display: flex;
  gap: 32px;
  /* Start with a gentle default; will be overridden by --duration */
  animation: marquee var(--duration, 120s) linear infinite;
  will-change: transform;
}

.track.paused {
  animation-play-state: paused;
}

@keyframes marquee {
  from {
    transform: translate3d(0, 0, 0);
  }

  to {
    transform: translate3d(calc(var(--cycle, 600px) * -1), 0, 0);
  }

  /* fallback */
}

.what-makes-us {
  /* matches your beige background nicely */
  background-color: transparent;
  /* page already has #F9EEE2 */
}

.difference-card {
  border-radius: 24px;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.difference-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.08);
}

/* round chip behind the icon */
.difference-icon {
  background-color: #f4f0ff;
  /* tweak to match your palette */
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.bg-hero-section {
  background: url('@/assets/imgs/coastline.png') center/cover no-repeat;
  position: relative;
}

.bg-hero-section::before {
  content: "";
  position: absolute;
  inset: 0;
  background-color: rgba(40, 46, 152, 0.7);
  /* dark overlay for text readability */
  z-index: 1;
}

.bg-hero-section>.v-container {
  position: relative;
  z-index: 2;
}

.white--text {
  color: #ffffff !important;
}
</style>
